import { radixStringToNumber } from "@zyzgroup/core-common";

export interface ByteArrayOutputStream {
  writeByte(b: number): void;
  writeShort(i: number): void;
  writeBytes(ab: number[], off: number, len: number): void;
  writeString(s: string): void;
  toByteArray(): number[];
  toString(): string;
}

export interface Base64EncodeOutputStream {
  writeByte(b: number): void;
  flush(): void;
  toString(): string;
}

export const byteArrayOutputStream = function () {
  const _bytes: number[] = [];

  return {
    writeByte(b) {
      _bytes.push(b & 0xff);
    },
    writeShort(i) {
      this.writeByte(i);
      this.writeByte(i >>> 8);
    },
    writeBytes(ab, off = 0, len = ab.length) {
      for (let i = 0; i < len; i++) {
        this.writeByte(ab[i + off]);
      }
    },
    writeString(s) {
      for (let i = 0, len = s.length; i < len; i++) {
        this.writeByte(s.charCodeAt(i));
      }
    },
    toByteArray() {
      return _bytes;
    },
    toString() {
      let s = "[";
      for (let i = 0, len = _bytes.length; i < len; i++) {
        if (i > 0) {
          s += ",";
        }
        s += _bytes[i];
      }
      s += "]";
      return s;
    }
  } as ByteArrayOutputStream;
};

export const base64EncodeOutputStream = function () {
  let _buffer = 0;
  let _buflen = 0;
  let _length = 0;
  let _base64 = "";

  const encode = function (n: number) {
    if (n < 0) {
      // error
    } else if (n < 26) {
      return 0x41 + n;
    } else if (n < 52) {
      return 0x61 + (n - 26);
    } else if (n < 62) {
      return 0x30 + (n - 52);
    } else if (n == 62) {
      return 0x2b;
    } else if (n == 63) {
      return 0x2f;
    }
    throw new Error("n:" + n);
  };

  const writeEncoded = function (b: number) {
    _base64 += String.fromCharCode(encode(b & 0x3f));
  };

  return {
    writeByte(n) {
      _buffer = (_buffer << 8) | (n & 0xff);
      _buflen += 8;
      _length += 1;
      while (_buflen >= 6) {
        writeEncoded(_buffer >>> (_buflen - 6));
        _buflen -= 6;
      }
    },
    flush() {
      if (_buflen > 0) {
        writeEncoded(_buffer << (6 - _buflen));
        _buffer = 0;
        _buflen = 0;
      }
      if (_length % 3 != 0) {
        // padding
        const padlen = 3 - (_length % 3);
        for (let i = 0; i < padlen; i++) {
          _base64 += "=";
        }
      }
    },
    toString() {
      return _base64;
    }
  } as Base64EncodeOutputStream;
};

export const gifImage = function (
  size: number,
  foreground: string,
  background: string
) {
  const _data = new Array(size * size);

  const bitOutputStream = function (out: ByteArrayOutputStream) {
    const _out = out;
    let _bitLength = 0;
    let _bitBuffer = 0;

    return {
      write(data: number, length: number) {
        if (data >>> length != 0) {
          throw new Error("length over");
        }

        while (_bitLength + length >= 8) {
          _out.writeByte(0xff & ((data << _bitLength) | _bitBuffer));
          length -= 8 - _bitLength;
          data >>>= 8 - _bitLength;
          _bitBuffer = 0;
          _bitLength = 0;
        }

        _bitBuffer = (data << _bitLength) | _bitBuffer;
        _bitLength = _bitLength + length;
      },

      flush() {
        if (_bitLength > 0) {
          _out.writeByte(_bitBuffer);
        }
      }
    };
  };

  const getLZWRaster = function (lzwMinCodeSize: number) {
    const clearCode = 1 << lzwMinCodeSize;
    const endCode = (1 << lzwMinCodeSize) + 1;
    let bitLength = lzwMinCodeSize + 1;

    // Setup LZWTable
    const table = lzwTable();

    for (let i = 0; i < clearCode; i++) {
      table.add(String.fromCharCode(i));
    }
    table.add(String.fromCharCode(clearCode));
    table.add(String.fromCharCode(endCode));

    const byteOut = byteArrayOutputStream();
    const bitOut = bitOutputStream(byteOut);

    // clear code
    bitOut.write(clearCode, bitLength);

    let dataIndex = 0;

    let s = String.fromCharCode(_data[dataIndex]);
    dataIndex += 1;

    while (dataIndex < _data.length) {
      const c = String.fromCharCode(_data[dataIndex]);
      dataIndex += 1;

      if (table.contains(s + c)) {
        s = s + c;
      } else {
        bitOut.write(table.indexOf(s), bitLength);

        if (table.size() < 0xfff) {
          if (table.size() == 1 << bitLength) {
            bitLength += 1;
          }

          table.add(s + c);
        }

        s = c;
      }
    }

    bitOut.write(table.indexOf(s), bitLength);

    // end code
    bitOut.write(endCode, bitLength);

    bitOut.flush();

    return byteOut.toByteArray();
  };

  const lzwTable = function () {
    const _map: Record<string, number> = {};
    let _size = 0;

    return {
      add(key: string) {
        if (this.contains(key)) {
          throw new Error("dup key:" + key);
        }
        _map[key] = _size;
        _size += 1;
      },

      size() {
        return _size;
      },

      indexOf(key: string) {
        return _map[key];
      },

      contains(key: string) {
        return typeof _map[key] != "undefined";
      }
    };
  };

  return {
    setPixel(x: number, y: number, pixel: number) {
      _data[y * size + x] = pixel;
    },
    write(out: ByteArrayOutputStream) {
      // GIF Signature
      out.writeString("GIF87a");

      // Screen Descriptor
      out.writeShort(size);
      out.writeShort(size);

      out.writeByte(0x80); // 2bit
      out.writeByte(0);
      out.writeByte(0);

      // Global Color Map

      // black
      out.writeByte(radixStringToNumber("0x" + foreground[1] + foreground[2]));
      out.writeByte(radixStringToNumber("0x" + foreground[3] + foreground[4]));
      out.writeByte(radixStringToNumber("0x" + foreground[5] + foreground[6]));

      // white
      out.writeByte(radixStringToNumber("0x" + background[1] + background[2]));
      out.writeByte(radixStringToNumber("0x" + background[3] + background[4]));
      out.writeByte(radixStringToNumber("0x" + background[5] + background[6]));

      // Image Descriptor
      out.writeString(",");
      out.writeShort(0);
      out.writeShort(0);
      out.writeShort(size);
      out.writeShort(size);
      out.writeByte(0);

      // Local Color Map

      // Raster Data
      const lzwMinCodeSize = 2;
      const raster = getLZWRaster(lzwMinCodeSize);

      out.writeByte(lzwMinCodeSize);

      let offset = 0;

      while (raster.length - offset > 255) {
        out.writeByte(255);
        out.writeBytes(raster, offset, 255);
        offset += 255;
      }

      out.writeByte(raster.length - offset);
      out.writeBytes(raster, offset, raster.length - offset);
      out.writeByte(0x00);

      // GIF Terminator
      out.writeString(";");
    }
  };
};

export const outputGifBase64 = function (
  size: number,
  foreground: string,
  background: string,
  getPixel: (x: number, y: number) => number
) {
  const gif = gifImage(size, foreground, background);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      gif.setPixel(x, y, getPixel(x, y));
    }
  }

  const b = byteArrayOutputStream();
  gif.write(b);

  const base64 = base64EncodeOutputStream();
  const bytes = b.toByteArray();
  for (let i = 0, len = bytes.length; i < len; i++) {
    base64.writeByte(bytes[i]);
  }
  base64.flush();

  let img = "";
  img += "data:image/gif;base64,";
  img += base64;

  return img;
};
